// src/app/(plataforma)/clientes/page.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import StarIcon from '@mui/icons-material/Star';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { API_BASE } from "../utils/endpoint";

// Configuração da API
const API_URL = API_BASE;

// Estilo para os modais
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto'
};

// Função para formatar moeda
const formatCurrency = (value) => 
  new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN',
    minimumFractionDigits: 2,
  }).format(value).replace('MZN', 'MT');

// --- Componente de Histórico Completo ---
const HistoricoCliente = ({ cliente, formatCurrency, onPagarDivida, onRegistrarCompra }) => {
    const [open, setOpen] = useState(false);
    const [abaAtiva, setAbaAtiva] = useState(0);

    const totalDivida = cliente.dividas?.reduce((acc, item) => acc + item.valor, 0) || 0;
    const totalCompras = cliente.compras?.reduce((acc, compra) => acc + compra.valor, 0) || 0;

    return (
        <>
            <IconButton 
                size="small"
                onClick={() => setOpen(true)}
                title="Ver Histórico Completo"
                color="info"
            >
                <HistoryIcon />
            </IconButton>
            
            <Modal open={open} onClose={() => setOpen(false)}>
                <Box sx={{...modalStyle, width: 800}}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Histórico de {cliente.nome}
                    </Typography>
                    
                    {/* Abas */}
                    <Tabs value={abaAtiva} onChange={(e, novaAba) => setAbaAtiva(novaAba)} sx={{ mb: 3 }}>
                        <Tab label={`Compras (${cliente.compras?.length || 0})`} />
                        <Tab label={`Dívidas (${cliente.dividas?.length || 0})`} />
                        <Tab label={`Pagamentos (${cliente.pagamentos?.length || 0})`} />
                    </Tabs>

                    {/* Conteúdo das Abas */}
                    {abaAtiva === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Histórico de Compras
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<AddIcon />}
                                    onClick={() => onRegistrarCompra(cliente.id)}
                                >
                                    Nova Compra
                                </Button>
                            </Box>
                            
                            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                {cliente.compras?.map((compra, index) => (
                                    <div key={index}>
                                        <ListItem 
                                            secondaryAction={
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {formatCurrency(compra.valor)}
                                                    </Typography>
                                                    <Chip 
                                                        label={compra.tipo} 
                                                        size="small" 
                                                        color={compra.tipo === 'Dívida' ? 'error' : 'success'}
                                                    />
                                                </Box>
                                            }
                                        >
                                            <ListItemText 
                                                primary={compra.descricao}
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            Data: {new Date(compra.data).toLocaleDateString('pt-BR')}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Produtos: {compra.itens?.join(', ') || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        <Divider />
                                    </div>
                                ))}
                                {(!cliente.compras || cliente.compras.length === 0) && (
                                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                        Nenhuma compra registrada
                                    </Typography>
                                )}
                            </List>
                        </Box>
                    )}

                    {abaAtiva === 1 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Dívidas Pendentes
                            </Typography>
                            {totalDivida > 0 ? (
                                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {cliente.dividas?.map((divida, index) => (
                                        <div key={index}>
                                            <ListItem 
                                                secondaryAction={
                                                    <Button 
                                                        variant="contained" 
                                                        color="success" 
                                                        startIcon={<PaymentIcon />}
                                                        onClick={() => onPagarDivida(cliente.id, index, divida.valor)}
                                                    >
                                                        Pagar
                                                    </Button>
                                                }
                                            >
                                                <ListItemText 
                                                    primary={`Dívida de ${formatCurrency(divida.valor)}`}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body2">
                                                                Data: {new Date(divida.data).toLocaleDateString('pt-BR')}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Origem: {divida.origem}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                        </div>
                                    ))}
                                </List>
                            ) : (
                                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                    Nenhuma dívida pendente
                                </Typography>
                            )}
                        </Box>
                    )}

                    {abaAtiva === 2 && (
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Histórico de Pagamentos
                            </Typography>
                            {cliente.pagamentos && cliente.pagamentos.length > 0 ? (
                                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {cliente.pagamentos.map((pagamento, index) => (
                                        <div key={index}>
                                            <ListItem 
                                                secondaryAction={
                                                    <Typography color="success.main" fontWeight="bold">
                                                        {formatCurrency(pagamento.valor)}
                                                    </Typography>
                                                }
                                            >
                                                <ListItemText 
                                                    primary={`Pagamento realizado`}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body2">
                                                                Data: {new Date(pagamento.data).toLocaleDateString('pt-BR')}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Referência: {pagamento.referencia}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                        </div>
                                    ))}
                                </List>
                            ) : (
                                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                                    Nenhum pagamento registrado
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="body2">Total Compras:</Typography>
                                <Typography variant="h6">{formatCurrency(totalCompras)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2">Dívida Atual:</Typography>
                                <Typography variant="h6" color={totalDivida > 0 ? 'error.main' : 'success.main'}>
                                    {formatCurrency(totalDivida)}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2">Total Pago:</Typography>
                                <Typography variant="h6" color="success.main">
                                    {formatCurrency(cliente.pagamentos?.reduce((acc, p) => acc + p.valor, 0) || 0)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Button onClick={() => setOpen(false)} variant="outlined" sx={{ mt: 3 }}>
                        Fechar
                    </Button>
                </Box>
            </Modal>
        </>
    );
}

// --- Componente Principal ---
export default function ClientesPage() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados para filtros
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [busca, setBusca] = useState('');

  // Estados para Modais
  const [openAddCliente, setOpenAddCliente] = useState(false);
  const [openRegistrarCompra, setOpenRegistrarCompra] = useState(false);
  const [openPagarDivida, setOpenPagarDivida] = useState(false);
  
  // Estados para formulários
  const [novoCliente, setNovoCliente] = useState({
    nome: "",
    contacto: "",
    contactoAlternativo: "", // NOVO CAMPO
    email: "", // NOVO CAMPO
    endereco: "",
  });
  
  const [compraData, setCompraData] = useState({
    clienteId: null,
    valor: "",
    descricao: "",
    itens: "",
    tipoPagamento: "divida"
  });

  const [pagarDividaData, setPagarDividaData] = useState({
    clienteId: null,
    dividaIndex: null,
    valor: "",
    referencia: ""
  });

  // Função para mostrar snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Buscar clientes da API
  const fetchClientes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/clientes`);
      const result = await response.json();
      
      if (result.success) {
        setClientes(result.data);
      } else {
        showSnackbar('Erro ao carregar clientes', 'error');
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      showSnackbar('Erro de conexão com o servidor', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  /* --- FUNÇÕES DE FILTRO --- */
  const clientesFiltrados = clientes.filter(cliente => {
    const matchBusca = cliente.nome.toLowerCase().includes(busca.toLowerCase()) ||
                      cliente.contacto.includes(busca) ||
                      (cliente.email && cliente.email.toLowerCase().includes(busca.toLowerCase())) || // BUSCA POR EMAIL
                      (cliente.contactoAlternativo && cliente.contactoAlternativo.includes(busca)); // BUSCA POR CONTACTO ALTERNATIVO
    
    const totalDivida = cliente.dividas?.reduce((acc, item) => acc + item.valor, 0) || 0;
    
    switch(filtroAtivo) {
      case 'devedores':
        return matchBusca && totalDivida > 0;
      case 'melhores':
        return matchBusca && totalDivida === 0 && (cliente.compras?.length || 0) >= 5;
      case 'semDividas':
        return matchBusca && totalDivida === 0;
      default:
        return matchBusca;
    }
  });

  /* --- FUNÇÕES DE CLIENTES --- */
  const adicionarCliente = async () => {
    if (!novoCliente.nome || !novoCliente.contacto) {
      showSnackbar('Nome e contacto são obrigatórios', 'warning');
      return;
    }

    // Validação básica de email (opcional)
    if (novoCliente.email && !novoCliente.email.includes('@')) {
      showSnackbar('Por favor, insira um email válido', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/clientes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(novoCliente),
      });

      const result = await response.json();

      if (result.success) {
        setClientes([...clientes, result.data]);
        setNovoCliente({ 
          nome: "", 
          contacto: "", 
          contactoAlternativo: "", 
          email: "", 
          endereco: "" 
        });
        setOpenAddCliente(false);
        showSnackbar('Cliente adicionado com sucesso');
      } else {
        showSnackbar(result.message || 'Erro ao adicionar cliente', 'error');
      }
    } catch (error) {
      console.error('Erro ao adicionar cliente:', error);
      showSnackbar('Erro de conexão com o servidor', 'error');
    }
  };

  /* --- FUNÇÕES DE COMPRAS --- */
  const handleRegistrarCompra = (clienteId) => {
    setCompraData({
      clienteId,
      valor: "",
      descricao: "",
      itens: "",
      tipoPagamento: "divida"
    });
    setOpenRegistrarCompra(true);
  };

  const confirmarCompra = async () => {
    if (!compraData.valor || !compraData.clienteId) {
      showSnackbar('Valor da compra é obrigatório', 'warning');
      return;
    }

    const valor = parseFloat(compraData.valor);
    if (isNaN(valor) || valor <= 0) {
      showSnackbar('Valor da compra deve ser maior que zero', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/clientes/${compraData.clienteId}/compras`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(compraData),
      });

      const result = await response.json();

      if (result.success) {
        // Atualizar a lista de clientes
        await fetchClientes();
        setOpenRegistrarCompra(false);
        setCompraData({ clienteId: null, valor: "", descricao: "", itens: "", tipoPagamento: "divida" });
        showSnackbar('Compra registrada com sucesso');
      } else {
        showSnackbar(result.message || 'Erro ao registrar compra', 'error');
      }
    } catch (error) {
      console.error('Erro ao registrar compra:', error);
      showSnackbar('Erro de conexão com o servidor', 'error');
    }
  };

  /* --- FUNÇÕES DE PAGAMENTO --- */
  const handlePagarDivida = (clienteId, dividaIndex, valor) => {
    setPagarDividaData({
      clienteId,
      dividaIndex,
      valor: valor.toString(),
      referencia: ""
    });
    setOpenPagarDivida(true);
  };

  const confirmarPagamento = async () => {
    if (!pagarDividaData.clienteId || pagarDividaData.dividaIndex === null) {
      showSnackbar('Dados de pagamento inválidos', 'warning');
      return;
    }

    const valor = parseFloat(pagarDividaData.valor);
    if (isNaN(valor) || valor <= 0) {
      showSnackbar('Valor do pagamento deve ser maior que zero', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/clientes/${pagarDividaData.clienteId}/pagamentos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pagarDividaData),
      });

      const result = await response.json();

      if (result.success) {
        // Atualizar a lista de clientes
        await fetchClientes();
        setOpenPagarDivida(false);
        setPagarDividaData({ clienteId: null, dividaIndex: null, valor: "", referencia: "" });
        showSnackbar('Pagamento registrado com sucesso');
      } else {
        showSnackbar(result.message || 'Erro ao registrar pagamento', 'error');
      }
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      showSnackbar('Erro de conexão com o servidor', 'error');
    }
  };

  /* --- ESTATÍSTICAS --- */
  const totalClientes = clientes.length;
  const totalDevedores = clientes.filter(c => (c.dividas?.length || 0) > 0).length;
  const totalDividas = clientes.reduce((acc, cli) => acc + (cli.dividas?.reduce((sum, d) => sum + d.valor, 0) || 0), 0);
  const totalVendas = clientes.reduce((acc, cli) => acc + (cli.compras?.reduce((sum, c) => sum + c.valor, 0) || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* CABEÇALHO */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          👥 Gestão de Clientes
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<PersonAddIcon />}
          onClick={() => setOpenAddCliente(true)}
        >
          Novo Cliente
        </Button>
      </Box>

      {/* ESTATÍSTICAS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clientes
              </Typography>
              <Typography variant="h4" component="div">
                {totalClientes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Clientes Devedores
              </Typography>
              <Typography variant="h4" component="div" color="error.main">
                {totalDevedores}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total em Dívidas
              </Typography>
              <Typography variant="h4" component="div" color="error.main">
                {formatCurrency(totalDividas)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total em Vendas
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {formatCurrency(totalVendas)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FILTROS E BUSCA */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buscar cliente (nome, contacto, email)..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Todos"
                onClick={() => setFiltroAtivo('todos')}
                color={filtroAtivo === 'todos' ? 'primary' : 'default'}
                variant={filtroAtivo === 'todos' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Devedores"
                onClick={() => setFiltroAtivo('devedores')}
                color={filtroAtivo === 'devedores' ? 'error' : 'default'}
                variant={filtroAtivo === 'devedores' ? 'filled' : 'outlined'}
                icon={<AttachMoneyIcon />}
              />
              <Chip
                label="Melhores Clientes"
                onClick={() => setFiltroAtivo('melhores')}
                color={filtroAtivo === 'melhores' ? 'success' : 'default'}
                variant={filtroAtivo === 'melhores' ? 'filled' : 'outlined'}
                icon={<StarIcon />}
              />
              <Chip
                label="Sem Dívidas"
                onClick={() => setFiltroAtivo('semDividas')}
                color={filtroAtivo === 'semDividas' ? 'success' : 'default'}
                variant={filtroAtivo === 'semDividas' ? 'filled' : 'outlined'}
                icon={<MoneyOffIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA DE CLIENTES */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 6 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Lista de Clientes ({clientesFiltrados.length})
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Contacto</strong></TableCell>
              <TableCell><strong>Contacto Alt.</strong></TableCell> {/* NOVA COLUNA */}
              <TableCell><strong>Email</strong></TableCell> {/* NOVA COLUNA */}
              <TableCell><strong>Endereço</strong></TableCell>
              <TableCell><strong>Compras</strong></TableCell>
              <TableCell><strong>Total em Dívida</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {clientesFiltrados.map((cli) => {
              const totalDivida = cli.dividas?.reduce((acc, item) => acc + item.valor, 0) || 0;
              const totalCompras = cli.compras?.length || 0;

              return (
                <TableRow key={cli.id} hover>
                  <TableCell>
                    <Box>
                      <Typography fontWeight="medium">{cli.nome}</Typography>
                      {totalCompras >= 5 && totalDivida === 0 && (
                        <Chip label="Melhor Cliente" size="small" color="success" icon={<StarIcon />} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{cli.contacto}</TableCell>
                  <TableCell>{cli.contactoAlternativo || '-'}</TableCell> {/* NOVA CÉLULA */}
                  <TableCell>{cli.email || '-'}</TableCell> {/* NOVA CÉLULA */}
                  <TableCell>{cli.endereco}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${totalCompras} compras`} 
                      size="small" 
                      variant="outlined" 
                      icon={<ShoppingCartIcon />}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: totalDivida > 0 ? 'error.main' : 'success.main' }}>
                    {formatCurrency(totalDivida)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleRegistrarCompra(cli.id)}
                      title="Registrar Compra"
                    >
                      <ReceiptIcon />
                    </IconButton>
                    
                    <HistoricoCliente 
                      cliente={cli} 
                      formatCurrency={formatCurrency}
                      onPagarDivida={handlePagarDivida}
                      onRegistrarCompra={handleRegistrarCompra}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      
      {/* --------------------- MODAIS --------------------- */}

      {/* Modal de Adicionar Cliente */}
      <Modal open={openAddCliente} onClose={() => setOpenAddCliente(false)}>
        <Box sx={{...modalStyle, width: 500}}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Cadastrar Novo Cliente
          </Typography>
          <TextField
            fullWidth
            label="Nome Completo *"
            value={novoCliente.nome}
            onChange={(e) => setNovoCliente({...novoCliente, nome: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Contacto Principal *"
            value={novoCliente.contacto}
            onChange={(e) => setNovoCliente({...novoCliente, contacto: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Contacto Alternativo (Opcional)"
            value={novoCliente.contactoAlternativo}
            onChange={(e) => setNovoCliente({...novoCliente, contactoAlternativo: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email (Opcional)"
            type="email"
            value={novoCliente.email}
            onChange={(e) => setNovoCliente({...novoCliente, email: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Endereço (Opcional)"
            value={novoCliente.endereco}
            onChange={(e) => setNovoCliente({...novoCliente, endereco: e.target.value})}
            sx={{ mb: 3 }}
          />
          <Button variant="contained" onClick={adicionarCliente} fullWidth>
            Guardar Cliente
          </Button>
        </Box>
      </Modal>

      {/* Modal de Registrar Compra */}
      <Dialog open={openRegistrarCompra} onClose={() => setOpenRegistrarCompra(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Nova Compra</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Valor da Compra (MT)"
            type="number"
            value={compraData.valor}
            onChange={(e) => setCompraData({...compraData, valor: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
            inputProps={{ min: "0.01", step: "0.01" }}
          />
          <TextField
            fullWidth
            label="Descrição da Compra"
            value={compraData.descricao}
            onChange={(e) => setCompraData({...compraData, descricao: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Itens (separados por vírgula)"
            value={compraData.itens}
            onChange={(e) => setCompraData({...compraData, itens: e.target.value})}
            sx={{ mb: 2 }}
            placeholder="Ex: Arroz, Feijão, Óleo"
          />
          <Typography variant="body2" sx={{ mb: 1 }}>
            Tipo de Pagamento:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={compraData.tipoPagamento === 'pago' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => setCompraData({...compraData, tipoPagamento: 'pago'})}
            >
              Pago à Vista
            </Button>
            <Button
              variant={compraData.tipoPagamento === 'divida' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => setCompraData({...compraData, tipoPagamento: 'divida'})}
            >
              Registrar como Dívida
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegistrarCompra(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={confirmarCompra}
            disabled={!compraData.valor}
          >
            Registrar Compra
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Pagar Dívida */}
      <Dialog open={openPagarDivida} onClose={() => setOpenPagarDivida(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Pagamento</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Valor da dívida: <strong>{formatCurrency(parseFloat(pagarDividaData.valor))}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Valor Pago (MT)"
            type="number"
            value={pagarDividaData.valor}
            onChange={(e) => setPagarDividaData({...pagarDividaData, valor: e.target.value})}
            sx={{ mb: 2 }}
            inputProps={{ min: "0.01", step: "0.01" }}
          />
          
          <TextField
            fullWidth
            label="Referência do Pagamento"
            value={pagarDividaData.referencia}
            onChange={(e) => setPagarDividaData({...pagarDividaData, referencia: e.target.value})}
            sx={{ mb: 2 }}
            placeholder="Ex: Transferência, Dinheiro, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPagarDivida(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={confirmarPagamento}
            startIcon={<PaymentIcon />}
          >
            Confirmar Pagamento
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}